import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { formatCurrency } from '../utils/formatCurrency';
import { Plus, Play, SkipForward, Pencil, Trash2, RefreshCcw, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';

const freqLabels = { daily: 'Diária', weekly: 'Semanal', monthly: 'Mensal', yearly: 'Anual' };

export default function RecurringTransactions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [categories, setCategories] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [form, setForm] = useState({
    name: '', type: 'expense', amount: '', currency: 'AOA', category_id: '', wallet_id: '',
    frequency: 'monthly', day_of_month: 1, is_active: true, auto_create: true,
  });

  const fetchItems = async () => {
    setLoading(true);
    const res = await api.get('/recurring-transactions');
    setItems(res.data.recurring_transactions || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();
    api.get('/categories').then(r => setCategories(r.data));
    api.get('/wallets').then(r => setWallets(r.data));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, amount: parseFloat(form.amount) };
    if (editItem) {
      await api.put(`/recurring-transactions/${editItem.id}`, payload);
    } else {
      await api.post('/recurring-transactions', payload);
    }
    setShowForm(false); setEditItem(null);
    fetchItems();
  };

  const handleProcess = async (id) => {
    await api.post(`/recurring-transactions/${id}/process`);
    fetchItems();
  };

  const handleSkip = async (id) => {
    await api.post(`/recurring-transactions/${id}/skip`);
    fetchItems();
  };

  const handleDelete = async (id) => {
    if (confirm('Eliminar esta transação recorrente?')) {
      await api.delete(`/recurring-transactions/${id}`);
      fetchItems();
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Transações Recorrentes</h1>
          <p className="page-subtitle">{items.filter(i => i.is_active).length} activas</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditItem(null); setForm({ name: '', type: 'expense', amount: '', currency: 'AOA', category_id: '', wallet_id: '', frequency: 'monthly', day_of_month: 1, is_active: true, auto_create: true }); setShowForm(true); }}>
          <Plus size={18} /> Nova Recorrente
        </button>
      </div>

      {loading ? (
        [1, 2, 3].map(i => <div key={i} className="skeleton mb-sm" style={{ height: 80, borderRadius: 16 }} />)
      ) : items.length === 0 ? (
        <div className="card"><div className="empty-state"><div className="empty-state-icon"><RefreshCcw size={28} /></div><p className="empty-state-title">Sem recorrentes</p></div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map(item => (
            <div key={item.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 42, height: 42, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: item.type === 'expense' ? 'var(--accent-red-dim)' : 'var(--accent-green-dim)', flexShrink: 0,
              }}>
                <RefreshCcw size={20} color={item.type === 'expense' ? 'var(--accent-red)' : 'var(--accent-green)'} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {item.name}
                  <span className={`badge ${item.is_active ? 'badge-active' : 'badge-paused'}`}>
                    {item.is_active ? 'Activa' : 'Inactiva'}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', display: 'flex', gap: 8, marginTop: 2 }}>
                  <span>{freqLabels[item.frequency]}</span>
                  <span>·</span>
                  <span>{item.category?.name}</span>
                  {item.next_due && (
                    <>
                      <span>·</span>
                      <span><Calendar size={11} style={{ verticalAlign: 'middle' }} /> {format(new Date(item.next_due), 'd MMM', { locale: pt })}</span>
                    </>
                  )}
                </div>
              </div>
              <div className={`transaction-amount ${item.type}`} style={{ minWidth: 100, textAlign: 'right' }}>
                {formatCurrency(item.amount)}
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button className="btn btn-ghost btn-icon" title="Lançar agora" onClick={() => handleProcess(item.id)}>
                  <Play size={14} />
                </button>
                <button className="btn btn-ghost btn-icon" title="Pular mês" onClick={() => handleSkip(item.id)}>
                  <SkipForward size={14} />
                </button>
                <button className="btn btn-ghost btn-icon" onClick={() => { setEditItem(item); setForm(item); setShowForm(true); }}>
                  <Pencil size={14} />
                </button>
                <button className="btn btn-ghost btn-icon" onClick={() => handleDelete(item.id)}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editItem ? 'Editar Recorrente' : 'Nova Recorrente'}</h2>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowForm(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Nome</label>
                  <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Tipo</label>
                    <select className="form-input form-select" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                      <option value="expense">Despesa</option>
                      <option value="income">Receita</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Valor</label>
                    <input className="form-input form-input-money" type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Frequência</label>
                    <select className="form-input form-select" value={form.frequency} onChange={e => setForm({ ...form, frequency: e.target.value })}>
                      {Object.entries(freqLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Dia do Mês</label>
                    <input className="form-input" type="number" min="1" max="31" value={form.day_of_month} onChange={e => setForm({ ...form, day_of_month: parseInt(e.target.value) })} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Categoria</label>
                    <select className="form-input form-select" value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })}>
                      <option value="">Selecionar...</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Carteira</label>
                    <select className="form-input form-select" value={form.wallet_id} onChange={e => setForm({ ...form, wallet_id: e.target.value })}>
                      <option value="">Selecionar...</option>
                      {wallets.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editItem ? 'Guardar' : 'Criar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
